/**
 * IssueDetailPanel - Slide-over panel for viewing and managing issue details.
 *
 * Shows when an issue is selected from the list. Contains tabs for:
 * - Details: Issue info, status change, assignment
 * - Comments: Threaded discussion
 * - Attachments: File uploads
 * - History: Audit trail (21 CFR Part 11 compliant)
 * - Links: Related/duplicate issues
 */

import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  AlertTriangle,
  MessageSquare,
  Paperclip,
  History,
  Link2,
  X,
  GitCompare,
  FileDown,
  Loader2,
  Download,
  StickyNote,
  Users,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { api } from '@/lib/api'
import { useToast } from '@/hooks/use-toast'
import { useAnnotationStore } from '@/stores/annotationStore'
import { useAnnotation, useMarkAsRead } from '@/hooks/useAnnotations'
import { useCurrentStudy } from '@/stores/studyStore'
import {
  ISSUE_STATUS_LABELS,
  ISSUE_STATUS_COLORS,
  ISSUE_PRIORITY_LABELS,
  ISSUE_PRIORITY_COLORS,
  ISSUE_CATEGORY_LABELS,
} from '@/types/annotation'
import { IssueDetailsTab } from './tabs/IssueDetailsTab'
import { CommentsTab } from './tabs/CommentsTab'
import { AttachmentsTab } from './tabs/AttachmentsTab'
import { HistoryTab } from './tabs/HistoryTab'
import { LinksTab } from './tabs/LinksTab'
import { SnapshotComparisonTab } from './tabs/SnapshotComparisonTab'

type PdfExportState = 'idle' | 'generating' | 'completed' | 'error'

export function IssueDetailPanel() {
  const { studyId: urlStudyId } = useParams<{ studyId: string }>()
  const currentStudy = useCurrentStudy()
  // Use URL param first (main domain), fallback to store (subdomain)
  const studyId = urlStudyId || currentStudy?.studyId

  const { toast } = useToast()

  // Store state
  const detailPanel = useAnnotationStore((state) => state.detailPanel)
  const closeDetailPanel = useAnnotationStore((state) => state.closeDetailPanel)
  const setDetailAnnotation = useAnnotationStore((state) => state.setDetailAnnotation)

  // PDF export state
  const [pdfExportState, setPdfExportState] = useState<PdfExportState>('idle')
  const [pdfTaskId, setPdfTaskId] = useState<string | null>(null)
  const [pdfDownloadUrl, setPdfDownloadUrl] = useState<string | null>(null)

  // Fetch annotation details (works for issues, self-notes, and role comments)
  const { data: issue, isLoading, error } = useAnnotation(studyId, detailPanel.annotationId)

  // Mark as read mutation
  const markAsRead = useMarkAsRead(studyId)

  // Reset PDF export state when panel closes or issue changes
  useEffect(() => {
    if (!detailPanel.isOpen) {
      setPdfExportState('idle')
      setPdfTaskId(null)
      setPdfDownloadUrl(null)
    }
  }, [detailPanel.isOpen])

  // Handle PDF export
  const handleExportPdf = async () => {
    if (!studyId || !detailPanel.annotationId) return

    setPdfExportState('generating')
    setPdfDownloadUrl(null)

    try {
      // Start PDF generation
      const response = await api.post(
        `/annotations/studies/${studyId}/annotations/issues/${detailPanel.annotationId}/export/pdf`
      )
      const taskId = response.data.taskId

      if (!taskId) {
        throw new Error('No task ID returned')
      }

      setPdfTaskId(taskId)

      // Poll for completion
      const pollInterval = setInterval(async () => {
        try {
          const statusResponse = await api.get(
            `/annotations/studies/${studyId}/annotations/issues/${detailPanel.annotationId}/export/pdf/status/${taskId}`
          )
          const status = statusResponse.data.status

          if (status === 'completed') {
            clearInterval(pollInterval)
            setPdfExportState('completed')
            setPdfDownloadUrl(statusResponse.data.downloadUrl)
            toast({
              title: 'PDF Ready',
              description: 'Your issue PDF is ready for download.',
            })
          } else if (status === 'failed') {
            clearInterval(pollInterval)
            setPdfExportState('error')
            toast({
              variant: 'destructive',
              title: 'PDF Generation Failed',
              description: statusResponse.data.error || 'Unknown error',
            })
          }
        } catch {
          clearInterval(pollInterval)
          setPdfExportState('error')
        }
      }, 2000) // Poll every 2 seconds

      // Stop polling after 2 minutes
      setTimeout(() => {
        clearInterval(pollInterval)
        if (pdfExportState === 'generating') {
          setPdfExportState('error')
          toast({
            variant: 'destructive',
            title: 'PDF Generation Timeout',
            description: 'PDF generation is taking too long. Please try again.',
          })
        }
      }, 120000)

    } catch (err) {
      setPdfExportState('error')
      toast({
        variant: 'destructive',
        title: 'PDF Export Failed',
        description: err instanceof Error ? err.message : 'Failed to start PDF generation',
      })
    }
  }

  // Handle PDF download
  const handleDownloadPdf = () => {
    if (!pdfDownloadUrl || !studyId || !detailPanel.annotationId || !pdfTaskId) return

    // Construct the full download URL
    const downloadUrl = `/api/v1/annotations/studies/${studyId}/annotations/issues/${detailPanel.annotationId}/export/pdf/download/${pdfTaskId}`

    // Open in new tab to trigger download
    window.open(downloadUrl, '_blank')
  }

  // Update store when issue data loads
  useEffect(() => {
    if (issue) {
      setDetailAnnotation(issue)
    }
  }, [issue, setDetailAnnotation])

  // Mark as read when opening
  useEffect(() => {
    if (detailPanel.isOpen && detailPanel.annotationId && studyId && issue && !issue.isRead) {
      markAsRead.mutate(detailPanel.annotationId)
    }
  }, [detailPanel.isOpen, detailPanel.annotationId, studyId, issue?.isRead])

  const handleClose = () => {
    closeDetailPanel()
  }

  return (
    <Sheet open={detailPanel.isOpen} onOpenChange={(open) => !open && handleClose()}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" aria-describedby="issue-detail-description">
        <SheetHeader className="space-y-1">
          <div className="flex items-center justify-between">
            <SheetTitle className="flex items-center gap-2">
              {issue?.annotationType === 'self_note' ? (
                <StickyNote className="h-5 w-5" />
              ) : issue?.annotationType === 'role_comment' ? (
                <Users className="h-5 w-5" />
              ) : (
                <AlertTriangle className="h-5 w-5" />
              )}
              {issue?.annotationType === 'self_note'
                ? `Note #${detailPanel.annotationId}`
                : issue?.annotationType === 'role_comment'
                  ? `Comment #${detailPanel.annotationId}`
                  : `Issue #${detailPanel.annotationId}`}
            </SheetTitle>
            <Button variant="ghost" size="icon" onClick={handleClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          <SheetDescription id="issue-detail-description">
            {issue?.annotationType === 'self_note'
              ? 'View and manage your personal note'
              : issue?.annotationType === 'role_comment'
                ? 'View and manage role comment'
                : 'View and manage issue details'}
          </SheetDescription>
        </SheetHeader>

        {!studyId ? (
          <div className="flex flex-col items-center justify-center py-12">
            <AlertTriangle className="h-12 w-12 text-destructive mb-4" />
            <p className="text-lg font-medium">No Study Selected</p>
            <p className="text-muted-foreground text-sm mt-1">
              Please select a study first
            </p>
          </div>
        ) : isLoading ? (
          <div className="space-y-4 mt-6">
            <Skeleton className="h-8 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-12">
            <AlertTriangle className="h-12 w-12 text-destructive mb-4" />
            <p className="text-lg font-medium">Error Loading Issue</p>
            <p className="text-muted-foreground text-sm mt-1">
              {error instanceof Error ? error.message : 'An error occurred'}
            </p>
          </div>
        ) : issue ? (
          <div className="mt-6 space-y-6">
            {/* Status Badges and Actions */}
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap gap-2">
                {issue.annotationType === 'self_note' && (
                  <Badge variant="secondary">Personal Note</Badge>
                )}
                {issue.annotationType === 'role_comment' && (
                  <Badge variant="secondary">Role Comment</Badge>
                )}
                {issue.status && issue.annotationType === 'issue' && (
                  <Badge className={ISSUE_STATUS_COLORS[issue.status]}>
                    {ISSUE_STATUS_LABELS[issue.status]}
                  </Badge>
                )}
                {issue.priority && issue.annotationType === 'issue' && (
                  <Badge className={ISSUE_PRIORITY_COLORS[issue.priority]}>
                    {ISSUE_PRIORITY_LABELS[issue.priority]}
                  </Badge>
                )}
                {issue.category && issue.annotationType === 'issue' && (
                  <Badge variant="outline">
                    {ISSUE_CATEGORY_LABELS[issue.category]}
                  </Badge>
                )}
                {!issue.isRead && (
                  <Badge variant="secondary">Unread</Badge>
                )}
              </div>

              {/* Export PDF Button - only for issues */}
              {issue.annotationType === 'issue' && (
                <div className="flex items-center gap-2">
                  {pdfExportState === 'idle' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleExportPdf}
                      className="gap-1.5"
                    >
                      <FileDown className="h-4 w-4" />
                      Export PDF
                    </Button>
                  )}
                  {pdfExportState === 'generating' && (
                    <Button variant="outline" size="sm" disabled className="gap-1.5">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Generating...
                    </Button>
                  )}
                  {pdfExportState === 'completed' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleDownloadPdf}
                      className="gap-1.5 text-green-600 border-green-600 hover:bg-green-50"
                    >
                      <Download className="h-4 w-4" />
                      Download PDF
                    </Button>
                  )}
                  {pdfExportState === 'error' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleExportPdf}
                      className="gap-1.5 text-destructive border-destructive hover:bg-destructive/10"
                    >
                      <FileDown className="h-4 w-4" />
                      Retry Export
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* Content Preview */}
            <div className="p-4 bg-muted rounded-lg">
              <p className="text-sm">{issue.content}</p>
            </div>

            {/* Data Context */}
            {(issue.datasetName || issue.fieldName) && (
              <div className="flex flex-wrap gap-2 text-sm">
                {issue.datasetName && (
                  <span className="font-mono bg-muted px-2 py-1 rounded">
                    {issue.datasetName}
                  </span>
                )}
                {issue.fieldName && (
                  <span className="font-mono bg-muted px-2 py-1 rounded">
                    {issue.fieldName}
                  </span>
                )}
              </div>
            )}

            {/* Tabs */}
            {issue.annotationType === 'issue' ? (
              <Tabs defaultValue="details" className="w-full">
                <TabsList className="grid w-full grid-cols-6">
                  <TabsTrigger value="details" className="text-xs">
                    Details
                  </TabsTrigger>
                  <TabsTrigger value="snapshot" className="text-xs flex items-center gap-1">
                    <GitCompare className="h-3 w-3" />
                    <span className="hidden sm:inline">Compare</span>
                  </TabsTrigger>
                  <TabsTrigger value="comments" className="text-xs flex items-center gap-1">
                    <MessageSquare className="h-3 w-3" />
                    <span className="hidden sm:inline">Comments</span>
                    {issue.commentCount > 0 && (
                      <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                        {issue.commentCount}
                      </Badge>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="attachments" className="text-xs flex items-center gap-1">
                    <Paperclip className="h-3 w-3" />
                    <span className="hidden sm:inline">Files</span>
                    {issue.attachmentCount > 0 && (
                      <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                        {issue.attachmentCount}
                      </Badge>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="history" className="text-xs flex items-center gap-1">
                    <History className="h-3 w-3" />
                    <span className="hidden sm:inline">History</span>
                  </TabsTrigger>
                  <TabsTrigger value="links" className="text-xs flex items-center gap-1">
                    <Link2 className="h-3 w-3" />
                    <span className="hidden sm:inline">Links</span>
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="details" className="mt-4">
                  <IssueDetailsTab issue={issue} studyId={studyId!} />
                </TabsContent>

                <TabsContent value="snapshot" className="mt-4">
                  <SnapshotComparisonTab issue={issue} studyId={studyId!} />
                </TabsContent>

                <TabsContent value="comments" className="mt-4">
                  <CommentsTab annotationId={issue.id} studyId={studyId!} />
                </TabsContent>

                <TabsContent value="attachments" className="mt-4">
                  <AttachmentsTab annotationId={issue.id} studyId={studyId!} />
                </TabsContent>

                <TabsContent value="history" className="mt-4">
                  <HistoryTab annotationId={issue.id} studyId={studyId!} />
                </TabsContent>

                <TabsContent value="links" className="mt-4">
                  <LinksTab annotationId={issue.id} studyId={studyId!} />
                </TabsContent>
              </Tabs>
            ) : (
              /* Simplified tabs for self-notes and role-comments */
              <Tabs defaultValue="details" className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="details" className="text-xs">
                    Details
                  </TabsTrigger>
                  <TabsTrigger value="comments" className="text-xs flex items-center gap-1">
                    <MessageSquare className="h-3 w-3" />
                    <span className="hidden sm:inline">Comments</span>
                    {issue.commentCount > 0 && (
                      <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                        {issue.commentCount}
                      </Badge>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="history" className="text-xs flex items-center gap-1">
                    <History className="h-3 w-3" />
                    <span className="hidden sm:inline">History</span>
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="details" className="mt-4">
                  <IssueDetailsTab issue={issue} studyId={studyId!} />
                </TabsContent>

                <TabsContent value="comments" className="mt-4">
                  <CommentsTab annotationId={issue.id} studyId={studyId!} />
                </TabsContent>

                <TabsContent value="history" className="mt-4">
                  <HistoryTab annotationId={issue.id} studyId={studyId!} />
                </TabsContent>
              </Tabs>
            )}
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  )
}

export default IssueDetailPanel
